/**
 * Focus Immersion — Daily Meditation with Pulsing Light
 * 
 * Brief guidance → Fade to dark → Softly pulsing light
 * User selects light color. Focus counter tracks attention shifts.
 * Minimal. Elegant. No cognitive demand.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check } from "lucide-react";

const LIGHT_COLORS = [
  { id: "warm", label: "Warm", color: "210,175,130", bg: "rgba(210,175,130,0.08)" },
  { id: "emerald", label: "Green", color: "52,211,153", bg: "rgba(52,211,153,0.08)" },
  { id: "violet", label: "Violet", color: "167,139,250", bg: "rgba(167,139,250,0.08)" },
  { id: "blue", label: "Blue", color: "96,165,250", bg: "rgba(96,165,250,0.08)" },
  { id: "rose", label: "Rose", color: "244,163,175", bg: "rgba(244,163,175,0.08)" },
  { id: "white", label: "White", color: "220,220,220", bg: "rgba(220,220,220,0.06)" },
];

const GUIDANCE_TEXTS = [
  { text: "Settle in…", delay: 1000, duration: 4000 },
  { text: "Let everything soften", delay: 6000, duration: 4000 },
  { text: "Bring your attention to the light", delay: 12000, duration: 4000 },
  { text: "When your mind wanders… gently return", delay: 18000, duration: 5000 },
  { text: "Simply rest here", delay: 25000, duration: 4000 },
];

const FocusImmersion = ({
  durationMinutes = 5,
  onComplete,
  onClose,
}) => {
  const [phase, setPhase] = useState("color-select"); // color-select, guidance, immersion, complete
  const [selectedColor, setSelectedColor] = useState(LIGHT_COLORS[0]);
  const [currentGuidance, setCurrentGuidance] = useState(null);
  const [focusCount, setFocusCount] = useState(0);
  const [showCounter, setShowCounter] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const startTimeRef = useRef(null);
  const timerRef = useRef(null);
  const guidanceTimersRef = useRef([]);

  const totalMs = durationMinutes * 60 * 1000;

  const startImmersion = useCallback(() => {
    setPhase("guidance");
    startTimeRef.current = Date.now();

    // Schedule guidance texts
    GUIDANCE_TEXTS.forEach((g) => {
      const showTimer = setTimeout(() => setCurrentGuidance(g.text), g.delay);
      const hideTimer = setTimeout(() => setCurrentGuidance(null), g.delay + g.duration);
      guidanceTimersRef.current.push(showTimer, hideTimer);
    });

    // Transition to pure immersion after guidance
    const immersionTimer = setTimeout(() => setPhase("immersion"), 32000);
    guidanceTimersRef.current.push(immersionTimer);

    // Elapsed timer
    timerRef.current = setInterval(() => {
      const now = Date.now();
      const ms = now - startTimeRef.current;
      setElapsed(ms);
      if (ms >= totalMs) {
        setPhase("complete");
      }
    }, 1000);
  }, [totalMs]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      guidanceTimersRef.current.forEach(clearTimeout);
    };
  }, []);

  useEffect(() => {
    if (phase === "complete" && timerRef.current) {
      clearInterval(timerRef.current);
    }
  }, [phase]);

  const handleFocusTap = () => {
    setFocusCount((c) => c + 1);
    setShowCounter(true);
    setTimeout(() => setShowCounter(false), 2000);
  };

  const progress = Math.min(100, (elapsed / totalMs) * 100);
  const c = selectedColor.color;

  const formatTime = (ms) => {
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      style={{ background: "#060606" }}
      data-testid="focus-immersion"
    >
      {/* Close button */}
      <div className="absolute top-6 left-6 z-30">
        <button
          onClick={onClose}
          className="p-2"
          style={{ color: "rgba(255,255,255,0.12)" }}
          data-testid="focus-close-btn"
        >
          <X size={20} />
        </button>
      </div>

      <AnimatePresence mode="wait">
        {/* COLOR SELECT */}
        {phase === "color-select" && (
          <motion.div
            key="color-select"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="flex-1 flex flex-col items-center justify-center px-8"
            data-testid="color-select-phase"
          >
            <p
              className="text-sm italic mb-10"
              style={{ fontFamily: "'Spectral', serif", color: "rgba(255,255,255,0.3)" }}
            >
              Choose the colour of your light
            </p>

            <div className="flex gap-4 mb-12">
              {LIGHT_COLORS.map((lc) => (
                <button
                  key={lc.id}
                  onClick={() => setSelectedColor(lc)}
                  className="relative w-12 h-12 rounded-full transition-all"
                  style={{
                    background: `radial-gradient(circle, rgba(${lc.color},0.3) 0%, rgba(${lc.color},0.05) 70%)`,
                    border: `2px solid rgba(${lc.color},${selectedColor.id === lc.id ? "0.5" : "0.1"})`,
                    boxShadow: selectedColor.id === lc.id ? `0 0 20px rgba(${lc.color},0.15)` : "none",
                  }}
                  data-testid={`color-${lc.id}`}
                >
                  {selectedColor.id === lc.id && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute inset-0 flex items-center justify-center"
                    >
                      <Check size={16} style={{ color: `rgba(${lc.color},0.7)` }} />
                    </motion.div>
                  )}
                </button>
              ))}
            </div>

            <p className="text-[10px] mb-8" style={{ color: "rgba(255,255,255,0.15)" }}>
              {selectedColor.label}
            </p>

            <button
              onClick={startImmersion}
              className="px-10 py-3 rounded-full text-sm transition-all"
              style={{
                background: `rgba(${c},0.08)`,
                border: `1px solid rgba(${c},0.15)`,
                color: `rgba(${c},0.5)`,
                fontFamily: "'Playfair Display', serif",
              }}
              data-testid="begin-immersion-btn"
            >
              Begin
            </button>
          </motion.div>
        )}

        {/* GUIDANCE + IMMERSION */}
        {(phase === "guidance" || phase === "immersion") && (
          <motion.div
            key="immersion"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2 }}
            className="flex-1 flex flex-col items-center justify-center relative"
            onClick={handleFocusTap}
            data-testid="immersion-phase"
          >
            {/* The Light — center pulsing orb */}
            <motion.div className="relative w-32 h-32">
              {/* Outer glow */}
              <motion.div
                className="absolute inset-[-50%] rounded-full"
                style={{
                  background: `radial-gradient(circle, rgba(${c},0.04) 0%, transparent 60%)`,
                }}
                animate={{
                  scale: [1, 1.08, 1],
                  opacity: [0.5, 0.8, 0.5],
                }}
                transition={{ duration: 7, repeat: 999, repeatType: "loop", ease: "easeInOut" }}
              />
              {/* Middle glow */}
              <motion.div
                className="absolute inset-[-20%] rounded-full"
                style={{
                  background: `radial-gradient(circle, rgba(${c},0.06) 0%, transparent 65%)`,
                }}
                animate={{
                  scale: [1.05, 0.95, 1.05],
                  opacity: [0.4, 0.7, 0.4],
                }}
                transition={{ duration: 5, repeat: 999, repeatType: "loop", ease: "easeInOut", delay: 1 }}
              />
              {/* Core light */}
              <motion.div
                className="absolute inset-4 rounded-full"
                style={{
                  background: `radial-gradient(circle, rgba(${c},0.12) 0%, rgba(${c},0.03) 50%, transparent 70%)`,
                }}
                animate={{
                  scale: [1, 1.06, 1],
                  opacity: [0.6, 1, 0.6],
                }}
                transition={{ duration: 4, repeat: 999, repeatType: "loop", ease: "easeInOut", delay: 0.5 }}
              />
              {/* Pin of light in center */}
              <motion.div
                className="absolute rounded-full"
                style={{
                  width: 6,
                  height: 6,
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  background: `rgba(${c},0.35)`,
                  boxShadow: `0 0 12px rgba(${c},0.2), 0 0 40px rgba(${c},0.08)`,
                }}
                animate={{
                  opacity: [0.5, 0.9, 0.5],
                }}
                transition={{ duration: 3, repeat: 999, repeatType: "loop", ease: "easeInOut" }}
              />
            </motion.div>

            {/* Guidance text */}
            <div className="absolute bottom-32 left-0 right-0 px-10">
              <AnimatePresence mode="wait">
                {currentGuidance && (
                  <motion.p
                    key={currentGuidance}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 2 }}
                    className="text-center text-sm italic"
                    style={{ fontFamily: "'Spectral', serif", color: `rgba(${c},0.3)` }}
                  >
                    {currentGuidance}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>

            {/* Focus counter — appears on tap */}
            <AnimatePresence>
              {showCounter && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.3 }}
                  className="absolute top-24 right-8"
                >
                  <div
                    className="px-3 py-1.5 rounded-full"
                    style={{
                      background: `rgba(${c},0.06)`,
                      border: `1px solid rgba(${c},0.1)`,
                    }}
                  >
                    <p className="text-[11px] text-center" style={{ color: `rgba(${c},0.4)` }}>
                      {focusCount}
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Persistent subtle counter hint */}
            {phase === "immersion" && focusCount === 0 && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 5, duration: 3 }}
                className="absolute top-20 text-center text-[10px]"
                style={{ color: "rgba(255,255,255,0.08)" }}
              >
                tap when your focus shifts
              </motion.p>
            )}

            {/* Bottom: timer + progress */}
            <div className="absolute bottom-8 left-8 right-8">
              <div className="h-[1px] rounded-full overflow-hidden mb-3" style={{ background: "rgba(255,255,255,0.04)" }}>
                <motion.div
                  className="h-full rounded-full"
                  style={{
                    width: `${progress}%`,
                    background: `rgba(${c},0.2)`,
                  }}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px]" style={{ color: "rgba(255,255,255,0.1)" }}>
                  {formatTime(elapsed)}
                </span>
                <span className="text-[10px]" style={{ color: "rgba(255,255,255,0.1)" }}>
                  {formatTime(totalMs)}
                </span>
              </div>
            </div>
          </motion.div>
        )}

        {/* COMPLETE */}
        {phase === "complete" && (
          <motion.div
            key="complete"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 2 }}
            className="flex-1 flex flex-col items-center justify-center px-8"
            data-testid="complete-phase"
          >
            {/* Soft glow */}
            <motion.div
              className="w-20 h-20 rounded-full mb-10"
              style={{
                background: `radial-gradient(circle, rgba(${c},0.1) 0%, transparent 70%)`,
              }}
              animate={{ scale: [1, 1.05, 1], opacity: [0.5, 0.8, 0.5] }}
              transition={{ duration: 4, repeat: 999, repeatType: "loop" }}
            />

            <p
              className="text-base italic mb-3 text-center"
              style={{ fontFamily: "'Playfair Display', serif", color: `rgba(${c},0.5)` }}
            >
              Well done
            </p>

            <p
              className="text-sm italic mb-8 text-center"
              style={{ fontFamily: "'Spectral', serif", color: "rgba(255,255,255,0.2)" }}
            >
              {formatTime(totalMs)} of presence
            </p>

            {/* Focus shift summary */}
            {focusCount > 0 && (
              <div
                className="px-5 py-3 rounded-xl mb-8"
                style={{ background: `rgba(${c},0.04)`, border: `1px solid rgba(${c},0.08)` }}
              >
                <p className="text-[11px] text-center" style={{ color: `rgba(${c},0.35)` }}>
                  Focus shifts noticed: <span style={{ color: `rgba(${c},0.6)` }}>{focusCount}</span>
                </p>
                <p className="text-[10px] text-center mt-1" style={{ color: "rgba(255,255,255,0.12)" }}>
                  Awareness of the shift is the practice
                </p>
              </div>
            )}

            <button
              onClick={onComplete}
              className="px-8 py-3 rounded-full text-sm transition-all"
              style={{
                background: `rgba(${c},0.08)`,
                border: `1px solid rgba(${c},0.12)`,
                color: `rgba(${c},0.4)`,
                fontFamily: "'Playfair Display', serif",
              }}
              data-testid="immersion-done-btn"
            >
              Done
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FocusImmersion;
